import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import Box from "@mui/material/Box";
import { getTheme } from "./theme";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";
import BackToTop from "./components/BackToTop";
import KonamiCode from "./components/KonamiCode";
import ChristmasOverlay from "./components/ChristmasOverlay";
import BSODEasterEgg from "./components/BSODEasterEgg";
import PasswordConfessional from "./components/PasswordConfessional";
import DeveloperNotes from "./components/DeveloperNotes";
import RayTracingMode from "./components/RayTracingMode";
import NeuromancerMode from "./components/NeuromancerMode";
import CSSLabLeak from "./components/CSSLabLeak";
import DeveloperConsole from "./components/DeveloperConsole";
import Home from "./pages/Home";
import Tournaments from "./pages/Tournaments";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import UserProfile from "./pages/UserProfile";
import AdminDashboard from "./pages/AdminDashboard";
import DiscordCallback from "./components/DiscordCallback";
import { pb, getCurrentUser } from "./services/pocketbase";
import { detectDevTools } from "./utils/devToolsWarning";
import { initTabClaustrophobia } from "./utils/tabClaustrophobia";
import Clips from "./pages/Clips";
import Discord from "./pages/Discord";
import FAQ from "./pages/FAQ";
import CodeOfConduct from "./pages/CodeOfConduct";
import Ranks from "./pages/Ranks";
import TermsAndPolicies from "./pages/TermsAndPolicies";
import StaffApplication from "./pages/StaffApplication";
import useContentProtection from "./hooks/useContentProtection";
import GameInterest from "./pages/GameInterest";
import NotFound from "./pages/NotFound";
import Leaderboard from "./pages/Leaderboard";
import BlackOps6Leaderboard from "./pages/BlackOps6Leaderboard";
import DNSHorrorStory from "./pages/DNSHorrorStory";

function App() {
  const [mode, setMode] = useState("dark");
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  // eslint-disable-next-line
  const [extendedLoadingActive, setExtendedLoadingActive] = useState(false);

  useContentProtection();

  useEffect(() => {
    detectDevTools();

    // Initialize Tab Claustrophobia feature
    initTabClaustrophobia();

    const initApp = async () => {
      const user = getCurrentUser();
      if (user?.theme_preference) {
        setMode(user.theme_preference);
      }

      setContentReady(true);

      // Determine if we should use extended loading (50% chance)
      const useExtendedLoading = Math.random() < 0.5;
      setExtendedLoadingActive(useExtendedLoading);

      // Base loading time is 1.5 seconds
      const baseLoadingTime = 1500;

      // If extended loading is active, add 2-4 more seconds
      const loadingTime = useExtendedLoading
        ? baseLoadingTime + (2000 + Math.random() * 2000)
        : baseLoadingTime;

      await new Promise((resolve) => setTimeout(resolve, loadingTime));
      setLoading(false);
    };

    initApp();

    // Tab title change functionality
    const originalTitle = document.title;
    const newTitle = " Michael, don't leave me!";
    let titleInterval;

    // Function to create scrolling title effect
    const createScrollingTitle = (text) => {
      let position = 0;
      return setInterval(() => {
        position = (position + 1) % text.length;
        document.title =
          text.substring(position) + " " + text.substring(0, position);
      }, 100);
    };

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User left the tab
        clearInterval(titleInterval);
        titleInterval = createScrollingTitle(newTitle);
      } else {
        // User returned to the tab
        clearInterval(titleInterval);
        document.title = originalTitle;
      }
    };

    // Add event listener for visibility change
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up on component unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(titleInterval);
      document.title = originalTitle;
    };
  }, []);

  const toggleTheme = async () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);

    const user = getCurrentUser();
    if (user) {
      try {
        await pb.collection("users").update(user.id, {
          theme_preference: newMode,
        });
      } catch (err) {
        console.error("Failed to save theme preference:", err);
      }
    }
  };

  // Add this function to ensure all links have proper href attributes for SEO
  useEffect(() => {
    // This runs after the component mounts and the DOM is ready
    const fixUncrawlableLinks = () => {
      // Find all anchor elements without href attributes
      const anchors = document.querySelectorAll('a:not([href]), a[href=""]');

      anchors.forEach((anchor) => {
        // If it's a React Router Link without href, add the current path
        if (!anchor.getAttribute("href")) {
          // Set a fallback href to make it crawlable
          // This won't affect React Router's behavior but helps SEO
          anchor.setAttribute(
            "href",
            anchor.getAttribute("to") || window.location.pathname
          );
        }
      });
    };

    // Run initially and whenever route changes
    fixUncrawlableLinks();

    // Create a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(fixUncrawlableLinks);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <LoadingScreen show={loading} />
      <Box
        sx={{
          visibility: contentReady ? "visible" : "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <ThemeProvider theme={getTheme(mode)}>
          <CssBaseline />

          <BrowserRouter>
            <Navigation onThemeToggle={toggleTheme} currentTheme={mode} />
            <KonamiCode />
            <ChristmasOverlay />
            <BSODEasterEgg />
            <PasswordConfessional />
            <DeveloperNotes />
            <RayTracingMode />
            <NeuromancerMode />
            <CSSLabLeak />
            <DeveloperConsole />
            <Box sx={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/events" element={<Tournaments />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/account" element={<Account />} />
                <Route path="/user/:username" element={<UserProfile />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/discord/callback" element={<DiscordCallback />} />
                <Route path="/clips" element={<Clips />} />
                <Route path="/upvote-leaderboard" element={<Leaderboard />} />
                <Route
                  path="/bo6-leaderboard"
                  element={<BlackOps6Leaderboard />}
                />
                <Route path="/discord" element={<Discord />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/code-of-conduct" element={<CodeOfConduct />} />
                <Route path="/ranks" element={<Ranks />} />
                <Route path="/policies/*" element={<TermsAndPolicies />} />
                <Route
                  path="/staff-application"
                  element={<StaffApplication />}
                />
                <Route path="/game-interest" element={<GameInterest />} />
                {/* Add the new DNS Horror Story route */}
                <Route path="/8.8.8.8" element={<DNSHorrorStory />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Box>
            <Footer />
            <BackToTop />
          </BrowserRouter>
        </ThemeProvider>
      </Box>
    </div>
  );
}

export default App;
